import { useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import moment from 'moment';
import { useFirebase } from '../components/FirebaseContextProvider';
import {
  useDivisions,
  useTournament,
  useTournamentParts,
} from '../utils/hooks';
import SchedulePrintView from '../components/SchedulePrintView';
import Spinner from '../components/Spinner';
import { buildColorLegend } from '../utils/colorLegend';
import { timeSlots } from '../utils/timeSlots';
import './PageSchedulePrint.scss';

export default function PageSchedulePrint() {
  const { tournamentId, dayNumber } = useParams();
  const { isTeamsSynced, userIsAdmin } = useFirebase();
  // Read by id, the same door `PageTournament` uses — the provider's array
  // holds only the caller's own organization and would say "not found"
  const { entity: tournament, isResolved } = useTournament(tournamentId);
  const arePartsReady = useTournamentParts(tournamentId, [
    'games',
    'fields',
    'teams',
  ]);
  const divisions = useDivisions(tournamentId);

  const colorLegend = useMemo(() => buildColorLegend(divisions), [divisions]);

  const sortedFields = useMemo(
    () =>
      tournament?.fields
        ? tournament.fields.toSorted((a, b) => a.sortIndex - b.sortIndex)
        : [],
    [tournament?.fields],
  );

  const dayIndex = Number(dayNumber);
  const date = useMemo(() => {
    if (!tournament?.startDate || !Number.isInteger(dayIndex) || dayIndex < 1)
      return null;
    return moment(tournament.startDate).add(dayIndex - 1, 'days');
  }, [tournament?.startDate, dayIndex]);

  const filteredGames = useMemo(() => {
    if (!tournament?.games || !date) return [];
    return tournament.games.filter((game) =>
      moment(game.date).isSame(date, 'day'),
    );
  }, [tournament?.games, date]);

  const loading = (
    <div className='page-schedule-print loading'>
      <Spinner show />
    </div>
  );

  // "Not found" is decided first and only from the document, never from the
  // subcollections: their loaded flags are read off the document itself, so a
  // tournament that will never arrive would keep them false and spin forever.
  if (!tournament) {
    if (!isResolved) return loading;

    return (
      <div className='page-schedule-print error'>
        <h1>Tournament not found</h1>
      </div>
    );
  }

  // It exists — now wait for the sheet itself. Nothing here waits on `users`:
  // coach and referee names travel on the team and game documents themselves.
  // Teams are waited on explicitly because they arrive in a third wave for
  // everyone now (tournament doc → its teams subcollection → the team
  // documents by id — there is no organization-wide team list any more), and
  // the games and the fields are the sheet, no longer in memory before somebody
  // asks for them. Printed early, this produces a sheet of TBD.
  if (!isTeamsSynced || !arePartsReady) return loading;

  if (!tournament.isSchedulePublished && !userIsAdmin) {
    return <Navigate to='/' replace />;
  }

  if (!date || date.isAfter(tournament.endDate, 'day')) {
    return (
      <div className='page-schedule-print error'>
        <h1>Day {dayNumber} is outside the tournament range</h1>
      </div>
    );
  }

  return (
    <div className='page-schedule-print'>
      <SchedulePrintView
        tournamentId={tournamentId}
        date={date}
        sortedFields={sortedFields}
        filteredGames={filteredGames}
        colorLegend={colorLegend}
        timeSlots={timeSlots}
      />
    </div>
  );
}
